/*
  <static-video> — click-to-play wrapper for the videos migrated off VideoWise.

  The instruction pages that came through a `video` setting use
  snippets/a-instruction-video-thumb.liquid together with the shared popup player in
  a-video-player-single.liquid. That player keys off fixed element ids, so it can only exist once
  per page — and several of the Instant sections appear more than once on the same template. This
  component is therefore self-contained: it shows the poster frame with the same outlined ring play
  button, and on click it turns the same <video> into a real player in place.

  Markup comes from snippets/a-static-video-thumb.liquid.

  Note on timing: the listener is bound unconditionally and the <video> is looked up lazily on
  click. Binding depends on children being parsed, and connectedCallback can run before that —
  which is exactly how the first version ended up doing nothing at all.
*/
if (!customElements.get('static-video')) {
  customElements.define(
    'static-video',
    class StaticVideo extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.onActivate = this.onActivate.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.addEventListener('click', this.onActivate);
        this.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onActivate);
        this.removeEventListener('keydown', this.onKeydown);
        this.bound = false;
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onActivate();
      }

      onActivate() {
        if (this.started) return;

        const video = this.querySelector('video');
        if (!video) return;
        this.started = true;

        video.controls = true;
        video.muted = false;
        video.setAttribute('preload', 'auto');

        const overlay = this.querySelector('.static-video-overlay');
        const badge = this.querySelector('.static-video-duration');
        if (overlay) overlay.hidden = true;
        if (badge) badge.hidden = true;

        this.classList.remove('cursor-pointer');
        this.removeAttribute('role');
        this.removeAttribute('tabindex');

        const played = video.play();
        // Autoplay with sound can be refused; falling back to muted keeps playback going, and the
        // controls are already visible either way.
        if (played && typeof played.catch === 'function') {
          played.catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      }
    }
  );
}
